import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '@/api/axios-client'
import { qk } from '@/api/query-keys'
import { flattenTeams } from '@/features/teams'
import type { Project, TeamNode } from '@/types/entities'

/**
 * The data and the rules behind the work-item pickers.
 *
 * Split from `pickers.tsx` rather than living beside the components because that file exports
 * components and this one exports hooks — mixing the two in a single module is what makes fast
 * refresh drop state on every edit.
 */

/** Selects cannot hold an empty-string value, so "none" gets a sentinel. */
export const NONE = 'none'

/**
 * The default target end TIME: 17:00, the end of a working day.
 *
 * The field is never empty, because an empty time next to a filled date is the one state that
 * reads as "no deadline" while actually meaning midnight — six hours earlier than anyone
 * expects, and enough to put a fresh item straight into backlog.
 */
export const DEFAULT_DUE_TIME = '17:00'

/** What the pickers need to know about a person — Member and AvatarPerson both satisfy it. */
export interface Person {
  id: string
  displayName: string
  photoUrl?: string | null
  disabled?: boolean
}

/**
 * The team tree, flattened into indented options.
 *
 * Returns the tree as well as the flat list: the flat list is what a `<Select>` renders, and
 * the tree is what "seed the approvers from the team's advisors" needs, because advisors hang
 * off the node rather than off the label.
 */
export function useTeamOptions(enabled = true) {
  const query = useQuery({
    queryKey: qk.teamTree(),
    queryFn: () => apiGet<{ tree: TeamNode[] }>('teams', { tree: true }),
    staleTime: 5 * 60_000,
    enabled,
  })
  const tree = useMemo(() => query.data?.tree ?? [], [query.data])
  const teams = useMemo(() => flattenTeams(tree), [tree])
  return { tree, teams, isPending: query.isPending && enabled }
}

/** Active projects only — an archived engagement takes no new work. */
export function useProjectOptions(enabled = true) {
  const query = useQuery({
    queryKey: qk.projects(),
    queryFn: () => apiGet<{ items: Project[] }>('projects'),
    staleTime: 60_000,
    enabled,
  })
  return { projects: query.data?.items ?? [], isPending: query.isPending && enabled }
}

/**
 * A team's advisors, narrowed to people who can actually be picked.
 *
 * Advisors are "not members; suggested as approvers on the team's items" — so a team that has
 * been reorganized can still name somebody who has since been disabled or removed. Suggesting
 * them would seed a slot with a value the Select cannot display.
 */
export function advisorsForTeam(
  tree: TeamNode[],
  teamId: string,
  eligible: Person[],
): string[] {
  const find = (nodes: TeamNode[]): TeamNode | undefined => {
    for (const node of nodes) {
      if (node.id === teamId) return node
      const hit = find(node.children)
      if (hit) return hit
    }
    return undefined
  }
  return (find(tree)?.advisorUserIds ?? []).filter((id) => eligible.some((p) => p.id === id))
}

/**
 * Guidance only, for the assignee-is-also-an-approver clash.
 *
 * The backend owns "the assignee cannot be an approver of their own work item" and its message
 * is what gets shown if the save is refused — this just names the clash up front instead of
 * silently dropping the assignee from the two lists, which would leave the reason invisible.
 */
export function approverClashMessage(
  ownerId: string,
  approver1Id: string,
  approver2Id: string | null,
  ownerName: string | undefined,
): string | null {
  if (!ownerId) return null
  if (approver1Id !== ownerId && approver2Id !== ownerId) return null
  return `${ownerName ?? 'The assignee'} is assigned to this item, so they cannot also approve it. Pick someone else, or assign it to somebody else.`
}
